"use client";

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import {
  MultiSelect,
  Pill,
  Flex,
  Text,
  ActionIcon,
  Tooltip,
  type ComboboxLikeRenderOptionInput,
  type ComboboxItem,
  type ComboboxRenderPillInput,
  Box as MantineBox,
  Text as MantineText,
} from "@mantine/core";
import { IconEye, IconEyeOff, IconCheck } from "@tabler/icons-react";
import sortBy from "lodash/sortBy";
import isEqual from "lodash/isEqual";
import xor from "lodash/xor";

import styles from "./styles.module.scss";

export type Option = {
  id: string | number;
  value?: string | number;
  label?: string;
  group?: string | null;
  sortOrder?: number;
};

type UngroupedOption = { label: string; value: string; sortOrder?: number };
type GroupedOption = { group: string; items: UngroupedOption[] };
type FormattedOption = UngroupedOption | GroupedOption;

interface InternalFilter {
  id: string | number;
  value?: string | number;
  label?: string;
  state?: string;
}

interface Props {
  label: string;
  options?: Option[];
  filterType: string;
  onSelect: (filterType: string, newSelection: Filter[]) => void;
  activeFilters: InternalFilter[];
  singleSelect?: boolean;
  groupSortOrder?: string[];
  allowExclude?: boolean;
  icon?: React.ReactNode;
  onInfoClick?: () => void;
  placeholder?: string;
}

const GroupedDropdown: React.FC<Props> = ({
  label,
  options,
  filterType,
  onSelect,
  activeFilters,
  singleSelect = false,
  allowExclude = true,
  groupSortOrder,
  icon,
  onInfoClick,
  placeholder,
}) => {
  const [value, setValue] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  // Mantine/floating-ui re-run the `flip` middleware any time the dropdown's
  // own rendered height changes — which happens on every keystroke while
  // searching, as the number of matching options shrinks or grows. That's
  // desired (the list should keep resizing to fit); what shouldn't happen is
  // `flip` using that resize as a reason to swap top/bottom mid-search. So:
  // let flip make its normal decision when the dropdown first opens, then
  // read the side it landed on and pin the position to exactly that for the
  // rest of this open session — resizing continues, flipping doesn't. On
  // close, the pin is released so the next open gets a fresh decision (e.g.
  // if the panel was scrolled in the meantime).
  const inputRef = useRef<HTMLInputElement>(null);
  const [lockedPosition, setLockedPosition] = useState<"top" | "bottom" | null>(null);

  const handleDropdownOpen = useCallback(() => {
    // Read on the next frame so floating-ui has resolved an initial
    // placement before we capture it (it's not necessarily available yet at
    // the exact moment the dropdown starts opening).
    requestAnimationFrame(() => {
      const input = inputRef.current;
      const dropdownId = input?.getAttribute("aria-controls");
      const controlled = dropdownId ? document.getElementById(dropdownId) : null;
      // aria-controls may point at the inner options list rather than the
      // positioned wrapper itself, so walk up to whichever ancestor actually
      // carries the resolved placement.
      const positioned = controlled?.closest<HTMLElement>("[data-position]");
      const placement = positioned?.getAttribute("data-position");
      if (placement) {
        setLockedPosition(placement.startsWith("top") ? "top" : "bottom");
      }
    });
  }, []);

  const handleDropdownClose = useCallback(() => {
    setLockedPosition(null);
  }, []);

  const isDesktop = useMediaQuery({ query: "(min-width: 850px)" });
  const truncateThreshold = isDesktop ? 20 : 28;

  const renderPill = useCallback(
    ({ option, onRemove, disabled }: ComboboxRenderPillInput<string>) => {
      const labelWithMarker = option?.label ?? "";
      const isExcluded = labelWithMarker.includes("​");
      const rawLabel = labelWithMarker.replace("​", "").trim();
      const isTruncated = rawLabel.length > truncateThreshold;
      const displayLabel = isTruncated ? `${rawLabel.slice(0, truncateThreshold)}…` : rawLabel;

      const pill = (
        <Pill
          withRemoveButton
          onRemove={onRemove}
          disabled={disabled}
          className={isExcluded ? styles.excludedPill : undefined}
        >
          {displayLabel}
        </Pill>
      );

      if (isTruncated) {
        return (
          <Tooltip label={rawLabel} withArrow zIndex={10000}>
            {pill}
          </Tooltip>
        );
      }
      return pill;
    },
    [truncateThreshold]
  );

  const idToName = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    options?.forEach((opt) => {
      const key = String(opt.value ?? opt.id);
      if (!key || !opt.label) {
        return;
      }
      map[key] = opt.label;
    });
    return map;
  }, [options]);

  const handleSearchChange = useCallback(
    (newSearchValue: string) => {
      setSearchValue(newSearchValue);
    },
    [setSearchValue]
  );

  const preserveSearchValue = useCallback(() => {
    setSearchValue(searchValue);
  }, [searchValue]);

  const handleValueChange = useCallback(
    (newValue?: string, newState?: "exclude" | "include") => {
      const newSet = new Set(value);
      if (newValue && !newState) {
        if (newSet.has(newValue)) {
          newSet.delete(newValue);
        } else {
          newSet.add(newValue);
        }
      }

      const newFilters: Filter[] = [...newSet]
        .map((idStr) => {
          if (!idStr) {
            return null;
          }
          const state = excluded.has(idStr) ? "exclude" : "include";
          const numericId = isNaN(Number(idStr)) ? parseInt(idStr, 10) || 0 : Number(idStr);
          return {
            id: numericId,
            value: idStr,
            label: idToName[idStr] || "",
            state: newState && idStr === newValue ? newState : state,
          } as Filter;
        })
        .filter(Boolean) as unknown as Filter[];

      onSelect(filterType, newFilters);
      if (!isEqual([...newSet], [value])) {
        const newArr = [...newSet].filter(Boolean);
        setValue(newArr);
      }
    },
    [excluded, filterType, idToName, onSelect, value]
  );

  // Toggles every real option in a group at once: selects all of them if
  // any are currently unselected, or clears all of them if the whole group
  // is already selected. A deliberate "select all" also clears any leftover
  // per-item exclude flags in the group, since the point is "everything
  // here is actively included" rather than "included but still shown as
  // struck through from an earlier toggle."
  const handleGroupSelectAll = useCallback(
    (groupName: string) => {
      const groupIds = (options ?? [])
        .filter((opt) => opt.group === groupName)
        .map((opt) => String(opt.value ?? opt.id));

      if (groupIds.length === 0) return;

      const allSelected = groupIds.every((id) => value.includes(id));

      const newValueSet = new Set(value);
      const newExcluded = new Set(excluded);

      if (allSelected) {
        groupIds.forEach((id) => newValueSet.delete(id));
      } else {
        groupIds.forEach((id) => {
          newValueSet.add(id);
          newExcluded.delete(id);
        });
      }

      const newArr = [...newValueSet];
      const newFilters: Filter[] = newArr
        .map((idStr) => {
          if (!idStr) return null;
          const numericId = isNaN(Number(idStr)) ? parseInt(idStr, 10) || 0 : Number(idStr);
          return {
            id: numericId,
            value: idStr,
            label: idToName[idStr] || "",
            state: newExcluded.has(idStr) ? "exclude" : "include",
          } as Filter;
        })
        .filter(Boolean) as unknown as Filter[];

      onSelect(filterType, newFilters);
      setValue(newArr);
      setExcluded(newExcluded);
    },
    [options, value, excluded, idToName, filterType, onSelect]
  );

  const handleClearAll = useCallback(() => {
    onSelect(filterType, []);
    setValue([]);
    setExcluded(new Set());
    setSearchValue("");
  }, [onSelect, filterType]);

  const handleOptionClear = useCallback(
    (newItemList: string[]) => {
      if (!newItemList?.length) {
        handleClearAll();
        return;
      }

      const removedItem = xor(value, newItemList);
      handleValueChange(removedItem[0]);
    },
    [handleValueChange, value, handleClearAll]
  );

  useEffect(() => {
    const newValue = activeFilters
      .map((filter) => filter?.value?.toString())
      .filter((v): v is string => typeof v === "string");

    const newExcluded = new Set(
      activeFilters
        .filter((filter) => filter.state === "exclude")
        .map((filter) => filter?.value?.toString())
        .filter(Boolean) as string[]
    );

    setValue((currentValue) => (isEqual(newValue, currentValue) ? currentValue : newValue));
    setExcluded((currentExcluded) =>
      isEqual(Array.from(newExcluded), Array.from(currentExcluded)) ? currentExcluded : newExcluded
    );
  }, [activeFilters]);

  const formattedOptions = useMemo<FormattedOption[]>(() => {
    if (!options?.length) return [];

    const map: Record<string, UngroupedOption[]> = {};
    options.forEach(({ id, value, label, group }) => {
      const optionValue = String(value ?? id);
      const entry: UngroupedOption = {
        label: excluded.has(optionValue) ? `${label}\u200B` : label || optionValue,
        value: optionValue,
        sortOrder: options.find((opt) => (opt.value ?? opt.id) === (value ?? id))?.sortOrder,
      };
      const key = group ?? "__ungrouped";

      if (!map[key]) map[key] = [];
      map[key].push(entry);
    });

    const ungrouped = map["__ungrouped"]
      ? sortBy(map["__ungrouped"], (item) => item.label.toLowerCase())
      : [];

    const groupEntries: GroupedOption[] = Object.keys(map)
      .filter((key) => key !== "__ungrouped")
      .map((groupName) => ({
        group: groupName,
        items: sortBy(map[groupName], (item) => item.label.toLowerCase()),
      }));

    let sorted = sortBy(
      sortBy([...ungrouped, ...groupEntries], (item) =>
        "group" in item ? item.group.toLowerCase() : item.label.toLowerCase()
      ),
      (item) => ("sortOrder" in item && item.sortOrder ? item.sortOrder : Infinity)
    );

    if (groupSortOrder && groupSortOrder.length > 0) {
      sorted = sortBy(sorted, (item) => {
        if ("group" in item) {
          return groupSortOrder.indexOf(item.group);
        }
        return Infinity;
      });
    }
    return sorted;
  }, [options, excluded, groupSortOrder]);

  const toggleExclude = useCallback((val: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);

      if (next.has(val)) {
        next.delete(val);
      } else {
        next.add(val);
      }

      return next;
    });
  }, []);

  const renderMultiSelectOption = (item: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
    const { option, checked } = item;
    const isExcluded = excluded.has(option.value);

    const onClickHandler = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleExclude(option.value);
      handleValueChange(option.value, !isExcluded ? "exclude" : "include");
    };

    return (
      <Flex
        gap="sm"
        align="center"
        w="100%"
        justify="space-between"
        pr={0}
        data-hideborder={!!groupSortOrder?.length}
      >
        <MantineBox className={styles.optionContent}>
          {checked && <IconCheck width="16px" height="16px" className={styles.checkIcon} />}
          <Text size="xs" ml="24px">
            {option.label.replace("\u200B", "").trim()}
          </Text>
        </MantineBox>
        {allowExclude && checked && (
          <Tooltip
            label={isExcluded ? "EXCLUDED" : "INCLUDED"}
            openDelay={500}
            withArrow
            zIndex={10000}
          >
            <ActionIcon
              onClick={onClickHandler}
              variant="transparent"
              size="xs"
              data-excluded={isExcluded}
              aria-hidden={true}
            >
              {isExcluded ? (
                <IconEyeOff size={16} color="black" />
              ) : (
                <IconEye size={16} color="black" />
              )}
            </ActionIcon>
          </Tooltip>
        )}
      </Flex>
    );
  };

  const customLabel = icon ? (
    <MantineBox className={styles.labelContainer}>
      <MantineText span inherit>
        {label}
      </MantineText>
      <ActionIcon
        type="button"
        onClick={onInfoClick}
        className={styles.infoButton}
        aria-label={`Open information for ${label}`}
        variant="transparent"
        size="sm"
      >
        {icon}
      </ActionIcon>
    </MantineBox>
  ) : (
    label
  );

  // Mantine gives group labels no click hook of their own — clicks on the
  // portalled dropdown still bubble through React's tree to this handler
  // (portals bubble via the component tree, not the DOM tree), so a click
  // landing on the label we tagged via classNames={{ groupLabel }} below
  // is caught here and treated as "select all in this group." Anything
  // else (an option, the input, a pill) just falls through untouched.
  const handleDropdownAreaClick = useCallback(
    (e: React.MouseEvent) => {
      const groupLabelEl = (e.target as HTMLElement).closest<HTMLElement>(`.${styles.groupLabel}`);
      if (groupLabelEl?.textContent) {
        handleGroupSelectAll(groupLabelEl.textContent);
      }
    },
    [handleGroupSelectAll]
  );

  return (
    <MantineBox className={styles.clearButtonWrapper} onClick={handleDropdownAreaClick}>
      <MultiSelect
        ref={inputRef}
        label={customLabel}
        aria-label={label}
        placeholder={!activeFilters?.length ? placeholder : ""}
        data={formattedOptions}
        searchable
        clearable={value?.length > 1}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onClear={handleClearAll}
        onDropdownOpen={handleDropdownOpen}
        onDropdownClose={handleDropdownClose}
        classNames={{ groupLabel: styles.groupLabel }}
        comboboxProps={{
          onOptionSubmit: (selected) => {
            handleValueChange(selected);
            preserveSearchValue();
          },
          position: lockedPosition ?? "bottom",
          // Once a side is locked (see handleDropdownOpen above), flip is
          // turned off so subsequent resizes while searching can't move it —
          // the dropdown still grows/shrinks with the result count, it just
          // can't switch sides anymore this session.
          middlewares: lockedPosition
            ? { flip: false, shift: false }
            : { flip: true, shift: false },
        }}
        onChange={handleOptionClear}
        value={value}
        renderOption={renderMultiSelectOption}
        renderPill={renderPill}
        maxDropdownHeight={250}
        withCheckIcon
        checkIconPosition="left"
        size="sm"
        chevronColor="black"
        pointer
        maxValues={singleSelect ? 1 : undefined}
      />
    </MantineBox>
  );
};

export default GroupedDropdown;
